import { mergeWith, cloneDeep } from 'lodash';

import { FieldMeta } from '@/modules/dynamic-form/models/FieldMeta.model';
import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
import { UnifiedFieldRecordKey } from '@/modules/dynamic-form/models/UnifiedFieldRecord.model';
import { createCompoundUnifiedFieldRecordValue } from '@/modules/dynamic-form/utils/form-field-helpers/formFieldHelpers';

function normalizeWithBackup(current: any, backup: any): any {
    if (typeof current !== 'object' || !backup) {
        return current;
    }
    const normalizeObject = (backupValue: any, currentValue: any) => {
        if (currentValue === null) {
            return backupValue;
        }
        return currentValue;
    };

    // current is object/array, fill nulls from backup sice we should not backup null values
    return mergeWith(cloneDeep(backup), current, normalizeObject);
}

export function performDefaultNotFoundToggle(
    data: {
        currentValue: any;
        beforeNotFoundValue: any;
        formId: string;
        fieldPath: FieldPath;
    },
    excutors: {
        setBeforeValueFunc: (param: { formId: string, path: FieldPath, value: any }) => void;
        setValueFunc: (param: { formId: string, path: FieldPath, value: any }) => void;
        setDisabledFunc: (param: {
            formId: string; path: FieldPath; value: Record<UnifiedFieldRecordKey, boolean>;
        }) => void;
    }): void {
    let newValue = data.currentValue;
    // if currently already not found, then reset value to previous value
    if (data.currentValue === null) {
        newValue = data.beforeNotFoundValue;
    } else {
        // if currently has value, then backup current value and set to not found
        excutors.setBeforeValueFunc({
            formId: data.formId,
            path: data.fieldPath,
            value: data.currentValue,
        });
        newValue = null;
    }
    excutors.setValueFunc({
        formId: data.formId,
        path: data.fieldPath,
        value: newValue,
    });
    // Generate disabled fields based on the structure of the current value
    const disabledValue = createCompoundUnifiedFieldRecordValue(
        data.currentValue,
        newValue === null,
    );

    excutors.setDisabledFunc({
        formId: data.formId,
        path: data.fieldPath,
        value: disabledValue,
    });
}

/**
 * Batch version of performDefaultNotFoundToggle that updates multiple fields at once
 * This is more efficient than calling performDefaultNotFoundToggle in a loop
 * as it only triggers store mutations once per type instead of once per field
 */
export function performBatchNotFoundToggle(
    data: {
        fields: {
            path: FieldPath;
            currentValue: any;
            beforeNotFoundValue: any;
            fieldMeta: FieldMeta;
        }[];
        formId: string;
        setToNotFound: boolean;
    },
    executors: {
        setBatchBeforeValuesFunc: (param: { formId: string, values: Record<FieldPath, any> }) => void;
        setBatchValuesFunc: (param: { formId: string, values: Record<FieldPath, any> }) => void;
        setBatchDisabledFunc: (param: {
            formId: string; disabledFields: Record<FieldPath, Record<UnifiedFieldRecordKey, boolean>>;
        }) => void;
    },
): void {
    const valuesToUpdate: Record<FieldPath, any> = {};
    const beforeNotFoundValuesToSave: Record<FieldPath, any> = {};
    const disabledFieldsToUpdate: Record<FieldPath, Record<UnifiedFieldRecordKey, boolean>> = {};

    data.fields.forEach((field) => {
        if (data.setToNotFound) {
            // Setting to not found: backup current value if it's not already null
            if (field.currentValue !== null) {
                // in case of object/array, they might have sub fields that were not found already,
                // we need to use previous backup value of it instead of saving null.
                beforeNotFoundValuesToSave[field.path] =
                    normalizeWithBackup(field.currentValue, field.beforeNotFoundValue);
            }
            valuesToUpdate[field.path] = null;
            // Generate disabled fields based on the structure of the current value
            disabledFieldsToUpdate[field.path] =
                createCompoundUnifiedFieldRecordValue(field.fieldMeta?.defaultValue, true);
        } else if (field.currentValue === null) {
            // Restoring from not found: use before value if current is null or has null leaves
            valuesToUpdate[field.path] = field.beforeNotFoundValue;
            // Generate disabled fields based on the structure of the before value (which is being restored)
            disabledFieldsToUpdate[field.path] =
                createCompoundUnifiedFieldRecordValue(field.fieldMeta?.defaultValue, false);
        }
    });

    // Only call store actions if there are values to update
    if (Object.keys(beforeNotFoundValuesToSave).length > 0) {
        executors.setBatchBeforeValuesFunc({
            formId: data.formId,
            values: beforeNotFoundValuesToSave,
        });
    }

    if (Object.keys(valuesToUpdate).length > 0) {
        executors.setBatchValuesFunc({
            formId: data.formId,
            values: valuesToUpdate,
        });
    }

    if (Object.keys(disabledFieldsToUpdate).length > 0) {
        executors.setBatchDisabledFunc({
            formId: data.formId,
            disabledFields: disabledFieldsToUpdate,
        });
    }
}
